import {
  IUseCase,
  GetDriverListUseCaseRequest,
  GetDriverDetailsUseCaseRequest,
  GetDriverPerformanceUseCaseRequest,
  GetDriverOnlineTimeUseCaseRequest,
} from './interfaces'
import { IDriverRepository } from '@/core/domain/repositories/IDriverRepository'
import { Driver, OnlineTimeData } from '@/core/domain/entities/Driver'
import { Result, createOk, createErr, isOk } from '@/shared/utils/Result'
import { ValidationError, NotFoundError } from '@/shared/errors'

export class GetDriverListUseCase
  implements IUseCase<GetDriverListUseCaseRequest, Driver[]>
{
  constructor(private readonly driverRepository: IDriverRepository) {}

  async execute(
    request: GetDriverListUseCaseRequest
  ): Promise<Result<Driver[], Error>> {
    try {
      // Validate input
      if (!request.userId || request.userId.trim() === '') {
        return createErr(new ValidationError('User ID is required'))
      }

      // Get drivers from repository
      const driversResult = await this.driverRepository.getDrivers(
        request.userId
      )

      if (!isOk(driversResult)) {
        return createErr(driversResult.error)
      }

      let drivers = driversResult.value

      // Apply filters if provided
      if (request.filters) {
        // Filter by status
        if (request.filters.status) {
          drivers = drivers.filter(
            (driver) => driver.status === request.filters!.status
          )
        }

        // Filter by search term (name, email, cpf)
        if (request.filters.search) {
          const searchTerm = request.filters.search.toLowerCase()
          drivers = drivers.filter(
            (driver) =>
              driver.fullName.toLowerCase().includes(searchTerm) ||
              driver.email.toLowerCase().includes(searchTerm) ||
              driver.cpf.includes(searchTerm)
          )
        }

        // Apply sorting
        if (request.filters.sortBy) {
          const sortOrder = request.filters.sortOrder === 'desc' ? -1 : 1
          drivers.sort((a, b) => {
            const field = request.filters!.sortBy as keyof Driver
            const aValue = (a[field] ?? '') as unknown
            const bValue = (b[field] ?? '') as unknown

            // Handle undefined values
            if (!aValue && !bValue) return 0
            if (!aValue) return 1 * sortOrder
            if (!bValue) return -1 * sortOrder

            if (aValue < bValue) return -1 * sortOrder
            if (aValue > bValue) return 1 * sortOrder
            return 0
          })
        }
      }

      return createOk(drivers)
    } catch (error) {
      console.error('GetDriverListUseCase failed:', error)
      return createErr(
        error instanceof Error ? error : new Error('Failed to get driver list')
      )
    }
  }
}

export class GetDriverDetailsUseCase
  implements IUseCase<GetDriverDetailsUseCaseRequest, Driver>
{
  constructor(private readonly driverRepository: IDriverRepository) {}

  async execute(
    request: GetDriverDetailsUseCaseRequest
  ): Promise<Result<Driver, Error>> {
    try {
      // Validate input
      if (!request.userId || request.userId.trim() === '') {
        return createErr(new ValidationError('User ID is required'))
      }

      if (!request.driverId || request.driverId.trim() === '') {
        return createErr(new ValidationError('Driver ID is required'))
      }

      // Get driver from repository
      const driverResult = await this.driverRepository.getDriver(
        request.userId,
        request.driverId
      )

      if (!isOk(driverResult)) {
        return createErr(driverResult.error)
      }

      if (!driverResult.value) {
        return createErr(new NotFoundError('Driver', request.driverId))
      }

      return createOk(driverResult.value)
    } catch (error) {
      console.error('GetDriverDetailsUseCase failed:', error)
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to get driver details')
      )
    }
  }
}

export class GetDriverPerformanceUseCase
  implements IUseCase<GetDriverPerformanceUseCaseRequest, Driver>
{
  constructor(private readonly driverRepository: IDriverRepository) {}

  async execute(
    request: GetDriverPerformanceUseCaseRequest
  ): Promise<Result<Driver, Error>> {
    try {
      if (!request.userId?.trim())
        return createErr(new ValidationError('User ID is required'))
      if (!request.driverId?.trim())
        return createErr(new ValidationError('Driver ID is required'))
      if (!request.period?.trim())
        return createErr(new ValidationError('Period is required'))

      const perfResult = await this.driverRepository.getDriverPerformance(
        request.userId,
        request.driverId,
        request.period
      )

      if (!isOk(perfResult)) return createErr(perfResult.error)

      return createOk(perfResult.value)
    } catch (error) {
      console.error('GetDriverPerformanceUseCase failed:', error)
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to get driver performance')
      )
    }
  }
}

export class GetDriverOnlineTimeUseCase
  implements IUseCase<GetDriverOnlineTimeUseCaseRequest, OnlineTimeData>
{
  constructor(private readonly driverRepository: IDriverRepository) {}

  async execute(
    request: GetDriverOnlineTimeUseCaseRequest
  ): Promise<Result<OnlineTimeData, Error>> {
    try {
      if (!request.userId?.trim())
        return createErr(new ValidationError('User ID is required'))
      if (!request.driverId?.trim())
        return createErr(new ValidationError('Driver ID is required'))
      if (!request.dateStart?.trim())
        return createErr(new ValidationError('Date start is required'))
      if (!request.dateEnd?.trim())
        return createErr(new ValidationError('Date end is required'))

      const onlineTimeResult = await this.driverRepository.getDriverOnlineTime(
        request.driverId,
        request.dateStart,
        request.dateEnd
      )

      if (!isOk(onlineTimeResult)) {
        return createErr(onlineTimeResult.error)
      }

      return createOk(onlineTimeResult.value)
    } catch (error) {
      console.error('GetDriverOnlineTimeUseCase failed:', error)
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to get driver online time')
      )
    }
  }
}
